import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import verified from '../assets/images/verified.svg';
import config from '../config/config';

const TipButtonCollection = ({ user, userId, walletBalance }) => {
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [paymentOption, setPaymentOption] = useState('paystack');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');

  useEffect(() => {
    setWordCount(message.length);
  }, [message]);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage('');
      }, 5000); 

      return () => clearTimeout(timer);
    }
  }, [toastMessage]);


  const handleOpenModal = () => {
    document.body.classList.add('modal-open-tip');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    document.body.classList.remove('modal-open-tip');
    setShowModal(false);
    setAmount('');
    setMessage('');
    setPaymentOption('paystack');
  };

  const handlePayment = async (e) => {
    e.preventDefault();
  
    // Set loading to true before the payment process starts
    setLoading(true);
  
    // Add transaction_id conditionally
    let transactionId = null;
  
    // Only add transaction_id for external payment methods
    if (paymentOption !== 'userwallet') {
      transactionId = `${paymentOption}-${new Date().getTime()}`;
    }
  
    const tipData = {
      tip_to_user_id: userId,
      tip_amount: amount,
      tip_comment: message,
      payment_method: paymentOption, 
      transaction_id: transactionId,  // Adding transaction ID
    };
  
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${config.baseURL}/send-tip`, tipData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (response.status === 200) {
        // Proceed with the payment handling based on the selected payment method
        if (paymentOption === 'paystack') {
          await handlePaystackPayment(response.data);
        } else if (paymentOption === 'nowpayments') {
          await handleNowPayments(response.data.transaction_id);
        } else if (paymentOption === 'userwallet') {
          await handleWalletPayment();
        }
      }
    } catch (error) {
      // Show error toast message
      setToastMessage('Failed to send tip. Please try again.');
      setToastType('error');
    } finally {
      // Set loading to false after the payment process is done
      setLoading(false);
    }
  };
  

const handleWalletPayment = async () => {
  try {
    await handleTransaction('userwallet');
    setToastMessage(`Tip sent to ${user.name} using wallet successfully!`);
    setToastType('success');
    handleCloseModal();
  } catch (error) {
    setToastMessage('Failed to process wallet payment. Please try again.');
    setToastType('error');
  }
};

const handlePaystackPayment = (responseData) => {
  console.log('Handling Paystack payment'); // Debug statement

  // Close the tip modal before opening the Paystack iframe
  handleCloseModal();

  const paystackConfig = {
    key: 'pk_test_6d954b11ef91c1d7a8911f46a866de5f37a5771a',
    email: responseData.email || 'default@example.com',
    amount: amount * 100, // Convert amount to kobo
    currency: 'NGN',
    reference: new Date().getTime().toString(),
    callback_url: 'https://yourwebsite.com/callback',
    onClose: () => {
      console.log('Payment window closed');
    },
    onSuccess: (response) => {
      console.log('Payment successful:', response);
      handleTransaction('paystack', response.reference);
      setToastMessage('Tip sent successfully!');
      setToastType('success');
      // No need to call handleCloseModal here, as it's already handled above
    },
  };

  // Load the Paystack script if it hasn't been loaded yet
  if (!window.PaystackPop) {
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.onload = () => {
      window.PaystackPop.setup(paystackConfig).openIframe();
    };
    document.body.appendChild(script);
  } else {
    window.PaystackPop.setup(paystackConfig).openIframe();
  }
};

  const handleNowPayments = async () => {
    console.log('Handling NowPayments'); // Debug statement

    const requestData = {
      price_amount: amount,
      price_currency: 'usd',
      pay_currency: 'btc',
      ipn_callback_url: 'https://yourwebsite.com/callback',
      order_id: `${userId}-${new Date().getTime()}`,
      order_description: `Tip for user ${userId}`,
    };

    try {
      const response = await axios.post(
        'https://api-sandbox.nowpayments.io/v1/payment',
        requestData,
        {
          headers: {
            'x-api-key': '11H5KAD-1NC4A54-NG25HG0-Y78N8BS',
            'Content-Type': 'application/json',
          }
        }
      );

      const { payment_url, payment_id } = response.data;

      if (payment_url) {
        window.open(payment_url, '_blank');
        handleTransaction('nowpayments', payment_id);
        setToastMessage('Tip sent successfully!');
        setToastType('success');
        handleCloseModal();
      } else {
        console.error('Failed to get payment URL');
        setToastMessage('Failed to get payment URL. Please try again.');
        setToastType('error');
      }
    } catch (error) {
      console.error('Error initiating NowPayments transaction:', error);
      setToastMessage('Error initiating payment. Please try again.');
      setToastType('error');
    }
  };

  const getInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  };

  const handleTransaction = async (method, transactionId) => {
    console.log(`Transaction with ${method}: ${transactionId}`);
    // Additional transaction handling can be added here
  };

  return (
    <>
      <button className="btn_tip_collection" onClick={handleOpenModal}>
        <svg
          width={38}
          height={38}
          viewBox="0 0 38 38"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.75 34.5C27.4485 34.5 34.5 27.4485 34.5 18.75C34.5 10.0515 27.4485 3 18.75 3C10.0515 3 3 10.0515 3 18.75C3 27.4485 10.0515 34.5 18.75 34.5Z"
            stroke="#242529"
            strokeWidth="3.09836"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14.3613 21.6491C14.3613 23.1093 15.5451 24.293 17.0053 24.293H19.4603C21.0248 24.293 22.2931 23.0247 22.2931 21.4602C22.2931 19.8957 21.0248 18.6275 19.4603 18.6275H17.1941C15.6296 18.6275 14.3613 17.3592 14.3613 15.7947C14.3613 14.2302 15.6296 12.9619 17.1941 12.9619H19.6492C21.1094 12.9619 22.2931 14.1456 22.2931 15.6058M18.3272 11.2622V12.9619M18.3272 24.293V25.9927"
            stroke="#242529"
            strokeWidth="2.58197"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Send a tip
      </button>


      {showModal && (
        <>
          <div className="modal-overlay" onClick={handleCloseModal} style={{ display: 'block' }}></div>
          <div className="modal restoreAccessModalAccess tip_popup fade show" style={{ display: 'block' }} tabIndex="-1" aria-labelledby="tipModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="tipModalLabel">Send Tip</h5>
                </div>
                <div className="modal-body">
                  {user && (
                    <div className="client-info d-flex tip_popup">
                      <div className="client_info_left">
                        <div className="client-img">
                          <div className="lazy-container">
                            <a 
                              className={`user_pic_area ${user.status === 'active' ? 'user_online_status' : ''}`} 
                              href={`/${user.username}`}
                            >
                              {user.profile ? (
                                <img src={user.profile} alt={user.name} /> 
                              ) : (
                                <span>{getInitials(user.name)}</span>
                              )}
                            </a>
                          </div>
                        </div>
                        <div className="content"> 
                          <h6 className="name">
                            <a href={`/${user.username}`}>
                              {user.name}                              
                              <img className="image_margin_left padding_remove" src={verified} alt="Verified" />                        
                            </a>
                          </h6>
                          <span className="designation">
                            <a href={`/${user.username}`}>@{user.username}</a>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <form id="sendTipForm" onSubmit={handlePayment}>
                    <div className="mb-2">
                        <div className="position_relative">
                          <div className="v-text-field__prefix">$</div>
                          <input
                            type="number"
                            className="form-control tip_amount"
                            id="amount"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder=""
                            required
                            min="5"
                          />
                          <label htmlFor="amount" className="placeholder-label tip_amount_only">Tip Amount</label>
                          <small className="minimum_amount">Minimum $5 USD</small>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-group payment_method_lable">
                          <label>Select Payment Method</label>
                           <select
                        className="form-select"
                        id="paymentOption"
                        value={paymentOption}
                        onChange={(e) => setPaymentOption(e.target.value)}  // Update paymentOption state
                        required  // This ensures the user selects an option
                      >
                        {/* Payment options */}
                        <option value="paystack">Paystack</option>
                        <option value="nowpayments">NowPayments</option>
                        {walletBalance > 0 && (
                          <option value="userwallet">
                            Wallet Balance (${walletBalance})
                          </option>
                        )} {/* Only render Wallet option if balance is greater than 0 */}
                      </select>
                        </div>
                    </div>
                    <div className="mb-3">
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Add a message (optional)"
                        value={message}
                        maxLength={1000}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                      <div className="word-count word_count_tip_popup">
                        <span>{wordCount} / 1000</span>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary send_btn" onClick={handleCloseModal}> Cancel </button>
                      <button 
  type="submit" 
  className="btn send_btn btn-primary"
  disabled={loading}  // Disable the button while loading is true
>
  {loading ? "Processing..." : "Send Tip"}  {/* Show "Processing..." while loading */}
</button>

                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

     {toastMessage && (
        <div className={`toastmessage ${toastType} ${toastMessage ? 'show' : ''}`}> 
          {toastMessage}
        </div>
      )}
      </>
  );
};

TipButtonCollection.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  altText: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  user: PropTypes.shape({
    user_name: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    profile: PropTypes.string,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

export default TipButtonCollection;
